import { useState, useEffect } from 'react';
import { FloorEvent, getFloorEvents } from '../../services/admin';

export default function ActivityLogs({ orgId }: { orgId: string }) {
  const [events, setEvents] = useState<FloorEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'request' | 'grant' | 'release'>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    loadEvents();
    // Refresh every 10 seconds
    const interval = setInterval(() => loadEvents(true), 5000);
    return () => clearInterval(interval);
  }, [orgId]);

  const loadEvents = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getFloorEvents(orgId, 500);
      setEvents(data);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const filteredEvents = events
    .filter((e) => filter === 'all' || e.event_type === `floor_${filter}`)
    .sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'floor_request':
        return '🤚';
      case 'floor_grant':
        return '✅';
      case 'floor_release':
        return '🛑';
      default:
        return '•';
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'floor_request':
        return 'text-yellow-400 bg-yellow-900/20';
      case 'floor_grant':
        return 'text-green-400 bg-green-900/20';
      case 'floor_release':
        return 'text-blue-400 bg-blue-900/20';
      default:
        return 'text-gray-400 bg-gray-900/20';
    }
  };

  const eventStats = {
    total: events.length,
    requests: events.filter(e => e.event_type === 'floor_request').length,
    grants: events.filter(e => e.event_type === 'floor_grant').length,
    releases: events.filter(e => e.event_type === 'floor_release').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">📋 Activity Logs</h2>
        <button
          onClick={() => loadEvents()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">Total Events</p>
          <p className="text-3xl font-bold">{eventStats.total}</p>
        </div>
        <div className="bg-yellow-900/30 rounded-lg p-4 border border-yellow-600">
          <p className="text-yellow-400 text-sm">Requests</p>
          <p className="text-3xl font-bold">{eventStats.requests}</p>
        </div>
        <div className="bg-green-900/30 rounded-lg p-4 border border-green-600">
          <p className="text-green-400 text-sm">Grants</p>
          <p className="text-3xl font-bold">{eventStats.grants}</p>
        </div>
        <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-600">
          <p className="text-blue-400 text-sm">Releases</p>
          <p className="text-3xl font-bold">{eventStats.releases}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <label className="text-sm font-medium text-gray-300">Filter:</label>
        {[
          { value: 'all' as const, label: 'All Events' },
          { value: 'request' as const, label: '🤚 Requests' },
          { value: 'grant' as const, label: '✅ Grants' },
          { value: 'release' as const, label: '🛑 Releases' },
        ].map((btn) => (
          <button
            key={btn.value}
            onClick={() => setFilter(btn.value)}
            className={`px-3 py-1 rounded text-sm font-medium transition ${
              filter === btn.value
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {btn.label}
          </button>
        ))}
        <span className="ml-auto"></span>
        <label className="text-sm font-medium text-gray-300">Sort:</label>
        <button
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded text-sm font-medium"
        >
          {sortOrder === 'desc' ? '↓ Newest' : '↑ Oldest'}
        </button>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading events...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          <p className="text-lg">No events found</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className={`flex items-start gap-4 p-4 rounded-lg border ${getEventColor(event.event_type)} border-current/30`}
            >
              <div className="text-2xl flex-shrink-0 mt-1">{getEventIcon(event.event_type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-bold">
                      {event.event_type === 'floor_request' && `${event.radio_name} requested floor on ${event.group_name}`}
                      {event.event_type === 'floor_grant' && `${event.radio_name} granted floor on ${event.group_name}`}
                      {event.event_type === 'floor_release' && `${event.radio_name} released floor on ${event.group_name}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(event.created_at).toLocaleString()}
                    </p>
                  </div>
                  {event.duration_ms && (
                    <div className="bg-black/30 px-2 py-1 rounded text-xs whitespace-nowrap">
                      {(event.duration_ms / 1000).toFixed(1)}s
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  ID: {event.id.substring(0, 8)}...
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-center text-sm text-gray-400 py-4 border-t border-gray-700">
        <p>Showing {filteredEvents.length} of {events.length} events</p>
        <p className="text-xs mt-2">Last refreshed: {new Date().toLocaleTimeString()}</p>
      </div>
    </div>
  );
}
